// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { AsyncPipe, NgIf } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  ElementRef,
  HostListener,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  combineLatest,
  distinctUntilChanged,
  map,
  startWith,
  take,
  tap,
} from 'rxjs';
import { ScreenLayoutService } from 'src/app/modules/shared/services/screen-layout-service/screen-layout.service';
import { MAX_RECORDS_TO_SHOW } from '../../../../config/recentSystem.constant';
import { SettingsService } from '../../../shared/services/settings.service';
import { DeepReadonly } from '../../../shared/utils/api-call-state.util';
import {
  SearchHistory,
  SearchHistoryService,
} from '../../search-history-service/search-history.service';
import { RouteService } from '../../services/routeService.service';
import { SystemOverviewService } from '../../services/systemOverview.service';
import { SearchResultsListComponent } from '../search-results-list/search-results-list.component';
import { SettingsComponent } from '../settings/settings.component';
import {
  SearchHistoryHighlighted,
  filterItems,
  keyItem,
} from './search-pane.utils';

@Component({
  selector: 'app-search-pane',
  templateUrl: './search-pane.component.html',
  styleUrls: ['./search-pane.component.scss'],
  standalone: true,

  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    NgIf,
    SettingsComponent,
    SearchResultsListComponent,
    AsyncPipe,
    TranslateModule,
  ],
})
export class SearchPaneComponent {
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('searchPopover') searchPopover?: ElementRef<Element>;
  recentHistPopupLabel = 'Recent Systems';

  @ViewChild('system') system?: ElementRef;
  MAX_ITEMS = MAX_RECORDS_TO_SHOW;

  keyboardInUse = false;

  activeModalities$ = combineLatest([
    this.settingsController.modalityIds$,
    this.systemOverviewService.modalities.lastSuccessfulData$,
  ]).pipe(
    map(([ids, all]) => {
      return all.map((modality) => ({
        ...modality,
        active: ids.indexOf(modality.id) !== -1,
      }));
    }),
  );

  recentSystems$ = this.searchHistoryService.history$;
  searchResults$ = this.systemOverviewService.systemSearch.data$;
  searching$ = this.systemOverviewService.systemSearch.loading$.pipe(
    tap((searching) => {
      // whenever a search is kicked off we switch to results tab
      if (searching) {
        this.selectedTab$.next(0);
      }
    }),
  );
  hasResults$ = this.searchResults$.pipe(map((results) => !!results));
  selectedTab$ = new BehaviorSubject(1);
  focusedElement$ = new BehaviorSubject<string>('body');
  focusedElementInstance$ = combineLatest({
    element: this.focusedElement$,
    searching: this.searching$,
    recentSystems: this.recentSystems$,
  }).pipe(
    map(
      ({ element }) =>
        this.componentRef?.nativeElement.querySelector(
          `#${element}`,
        ) as HTMLInputElement,
    ),
  );

  // current item index or undefined
  activeDescendant$ = this.focusedElementInstance$.pipe(
    map((element) => {
      if (element === this.searchInput?.nativeElement) {
        return null;
      }
      return element?.id || null;
    }),
  );
  searchPageOpen$ = this.routeService.searchPageOpen$;
  searchPageOpen = false;

  searchString$ = this.routeService.searchString$;

  /**
   * items for the respective tabs
   * 0: search results
   * 1: recent systems filtered by search string
   * 2: favs filtered by search string
   */
  items$: Observable<DeepReadonly<SearchHistoryHighlighted> | undefined> =
    combineLatest({
      selectedTab: this.selectedTab$,
      recentSystems: this.recentSystems$,
      searchResults: this.searchResults$.pipe(startWith(undefined)),
      searchString: this.searchString$,
    }).pipe(
      map(({ selectedTab, recentSystems, searchResults, searchString }) => {
        let items: SearchHistory = [];
        if (searchResults && selectedTab === 0) {
          // transform to SearchHistoryItem as well
          items = searchResults.map((item) => ({
            ...keyItem(item),
            // we also need to pull the bookmark state explicitly here
            // since the api result has no bookmark concept
            // and the bookmarks are rather stored on the client
            // the recent systems below inherently have bookmark states already
            // as they come from localstorage
            bookmark: recentSystems.find(
              ({ systemSerialNumber, systemMaterialNumber }) =>
                item.systemMaterialNumber === systemMaterialNumber &&
                item.systemSerialNumber === systemSerialNumber,
            )?.bookmark,
            unsupported: !item.isSeSOSupported,
            utcDate: '',
          }));
        }
        if (selectedTab === 1) {
          items = recentSystems;
        }
        if (selectedTab === 2) {
          items = recentSystems.filter((item) => item.bookmark);
        }
        return (this.lastItems = filterItems(items, searchString));
      }),
      distinctUntilChanged(),
    );
  lastItems?: DeepReadonly<SearchHistoryHighlighted>;

  searchTrigger$ = new ReplaySubject<unknown>(1);
  // if any of these changes we need to run a new search
  searchInputs$ = combineLatest({
    modalityIds: this.settingsController.modalityIds$,
    trigger: this.searchTrigger$,
  });

  // keep showing while changing them
  modalitySettingsWereShown$ = new BehaviorSubject(false);
  modalitySettingsWereHiddenByUser$ = new BehaviorSubject(false);

  // true if we have results for multiple modalities
  // or now results at all
  showModalitySettings$ = combineLatest({
    items: this.items$.pipe(startWith(undefined)),
    results: this.searchResults$,
    searching: this.searching$.pipe(startWith(false)),
  }).pipe(
    map(({ results, items, searching }) => {
      if (!results) {
        return true;
      }
      if (searching) {
        return false;
      }

      if (!items) {
        return false;
      }

      // does the result list contain results for multiple modalities?
      // const hasMultipleModalities = () =>
      //   Object.keys(
      //     items?.reduce(
      //       (acc, { modalityId }) => ({ ...acc, [modalityId!]: true }),
      //       {}
      //     ) || {}
      //   ).length > 0;
      const show = items?.length !== 1; //|| hasMultipleModalities();
      if (show) {
        this.modalitySettingsWereShown$.next(show);
      }
      return show;
    }),
  );

  gridIndex(element: Element | string): { x: number; y: number } {
    if (typeof element === 'string') {
      element = this.searchPopover?.nativeElement.querySelector(
        `#${element}`,
      ) as Element;
    }
    let x = 0;
    let y = 0;
    let found = false;
    if (element) {
      this.virtualGrid.forEach((row, y_) => {
        const index = row.indexOf(element);
        if (index !== -1) {
          x = index;
          y = y_;
          found = true;
        }
      });
    }
    return found ? { x, y } : { x: 0, y: 0 };
  }

  applyShuiHacks(): void {
    // hack to make sure the search icon cannot be focused
    const icon =
      this.componentRef?.nativeElement.querySelector('[icon="search"]');
    icon?.setAttribute('tabindex', '-1');
    // hack to make sure nothing has tabindex!="-1"
    this.searchPopover?.nativeElement
      .querySelectorAll('[tabindex]')
      .forEach((el: Element) => {
        if (el.hasAttribute('tabindex')) {
          el.setAttribute('tabindex', '-1');
        }
      });

    this.searchPopover?.nativeElement
      .querySelectorAll('[favorite]')
      .forEach((el: Element) => {
        el?.setAttribute('role', 'option');
      });
  }

  get virtualGrid(): Element[][] {
    let grid: Element[][] = [];
    const popover = this.searchPopover?.nativeElement;
    let options: Element[] = [];
    let tabs: Element[] = [];
    let items: Element[] = [];

    this.applyShuiHacks();
    if (popover) {
      const items_ = popover.querySelectorAll('[keyboard-focusable]');
      options = Array.from(items_).filter((item) =>
        item.getAttribute('id')?.startsWith('option-'),
      );
      tabs = Array.from(items_).filter((item) =>
        item.getAttribute('id')?.startsWith('tab-'),
      );

      items = Array.from(items_).filter((item) =>
        item.getAttribute('id')?.startsWith('item'),
      );
      grid = [
        [this.searchInput!.nativeElement],
        ...(tabs?.length ? [tabs] : []),
        ...(options?.length ? [options] : []),
        ...(items?.length
          ? items.map((item) => [
              item,
              item.parentNode!.querySelector('[favorite]')!,
            ])
          : []),
      ];
    }

    // console.log('grid', grid);
    return grid;
  }

  // transient state
  constructor(
    protected settingsController: SettingsService,
    protected searchHistoryService: SearchHistoryService,
    protected routeService: RouteService,
    protected router: Router,
    private componentRef: ElementRef,
    protected systemOverviewService: SystemOverviewService,
    protected screenLayoutService: ScreenLayoutService,
  ) {
    this.focusedElementInstance$
      .pipe(takeUntilDestroyed())
      .subscribe((element) => {
        // it is important to do this only after the dom has been updated
        requestAnimationFrame(() => {
          this.virtualGrid.forEach((row) => {
            row.forEach((el) => {
              el = componentRef.nativeElement.querySelector(
                `#${el.id}`,
              ) as Element;
              if (el !== this.searchInput?.nativeElement) {
                el.setAttribute(
                  'aria-selected',
                  el.id == element?.id ? `true` : 'false',
                );
              }
            });
          });
        });
      });

    // trigger actual search!
    // not triggered by changes to search string per se (but by pressing enter, i.e. search trigger)
    this.searchInputs$
      .pipe(takeUntilDestroyed())
      .subscribe(({ modalityIds }) => {
        // important:
        // we do not use this.systemOverviewService.systemSearch.data$ here
        // because it likely has an old value. we are only interested in the NEXT one here!
        const systemSerialNumber =
          this.routeService.searchState?.q?.trim() || '';
        this.executeSearch(systemSerialNumber, modalityIds);
      });

    this.routeService.initialQueryParams$
      .pipe(takeUntilDestroyed())
      .subscribe((params) => {
        if (params?.q) {
          this.selectedTab$.next(0);
          this.triggerSearch();
        }
      });

    this.searchPageOpen$
      .pipe(takeUntilDestroyed(), distinctUntilChanged())
      .subscribe((open) => {
        this.applyShuiHacks();
        this.searchPageOpen = open;
        if (open) {
          this.focusedElement$.next(this.searchInput!.nativeElement.id);
          this.modalitySettingsWereShown$.next(false);
          this.triggerSearch();
        }
      });
  }

  @HostListener('window:keydown', ['$event'])
  keydown(e: KeyboardEvent): void {
    if (e.key === 'ArrowRight') {
      this.keyboardInUse = true;
      const pos = this.gridIndex(this.focusedElement$.value);
      const rowIndex = pos.y % this.virtualGrid.length;
      if (rowIndex > 0) {
        const row = this.virtualGrid[rowIndex];
        this.focusedElement$.next(row[(pos.x + 1) % row.length].id);
        e.preventDefault();
      }
    } else if (e.key === 'ArrowLeft') {
      this.keyboardInUse = true;
      const pos = this.gridIndex(this.focusedElement$.value);
      const rowIndex = pos.y % this.virtualGrid.length;
      if (rowIndex > 0) {
        const row = this.virtualGrid[rowIndex];
        this.focusedElement$.next(
          row[(pos.x - 1 + row.length) % row.length].id,
        );
        e.preventDefault();
      }
    } else if (e.key === 'ArrowDown') {
      this.keyboardInUse = true;
      e.preventDefault();
      if (!this.searchPageOpen) {
        setTimeout(() => {
          const currentActiveEl = document.activeElement as HTMLElement;
          if (currentActiveEl.id === this.searchInput?.nativeElement.id) {
            this.routeService.openSearch(false);
            this.focusedElement$.next(this.virtualGrid[0][0].id);
          }
        });
      } else {
        const pos = this.gridIndex(this.focusedElement$.value);
        const rowIndex = (pos.y + 1) % this.virtualGrid.length;
        const row = this.virtualGrid[rowIndex];
        let el = row[0].id;
        // in case there is an enabled tab, pick that one first
        const enabled = row.find((el) => {
          return el.id.startsWith('tab-') && el.hasAttribute('active');
        })?.id;
        el = enabled || el;
        this.focusedElement$.next(el);
      }
    } else if (e.key === 'ArrowUp') {
      this.keyboardInUse = true;
      e.preventDefault();

      const pos = this.gridIndex(this.focusedElement$.value);
      const rowIndex =
        (pos.y - 1 + this.virtualGrid.length) % this.virtualGrid.length;
      const row = this.virtualGrid[rowIndex];
      let el = row[0].id;
      // in case there is an enabled tab, pick that one first
      const enabled = row.find((el) => {
        return el.id.startsWith('tab-') && el.hasAttribute('active');
      })?.id;
      el = enabled || el;
      this.focusedElement$.next(el);
    } else if (e.key === 'Tab') {
      if (e.target === this.searchInput?.nativeElement) {
        requestAnimationFrame(() => this.closeSearch());
      } else {
        this.keyboardInUse = true;
      }
    } else if (e.key === 'Enter') {
      this.keyboardInUse = true;
    } else {
      this.focusedElement$.next(this.searchInput!.nativeElement.id);
    }
    this.componentRef?.nativeElement
      ?.querySelector('#' + this.focusedElement$.value)
      ?.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
      });
  }

  pressEnter(e: KeyboardEvent): void {
    const el = this.componentRef?.nativeElement.querySelector(
      `#${this.focusedElement$.value}`,
    ) as HTMLElement & { active: boolean };

    // ignore leading and trailing whitespaces
    if (el.id === this.searchInput?.nativeElement.id) {
      this.searchTrigger$.next(undefined);
    } else if (el.id.startsWith('tab-') && !el.hasAttribute('disabled')) {
      this.selectedTab$.next(parseInt(el?.getAttribute('data-index') || '0'));
    } else if (el.tagName === 'SH-CHECKBOX') {
      el.active = !el.active;
      e.preventDefault();
    } else if (el.tagName === 'SH-ICON') {
      el.click();
      e.preventDefault();
    } else {
      if (el.id.startsWith('item')) {
        const itemKey = Number(el.id.substring(4));
        const selectedItem = this.lastItems?.find(
          ({ _key }: { _key: number }) => _key === itemKey,
        );
        if (selectedItem && !selectedItem.unsupported) {
          this.goToSystem(selectedItem);
        }
      } else {
        this.triggerSearch();
      }
    }
  }

  // executes search and performs automatic behavior
  executeSearch(systemSerialNumber: string, modalityIds: number[]): void {
    if (systemSerialNumber) {
      this.systemOverviewService.systemSearch
        .request({ systemSerialNumber, modalityIds })
        .pipe(
          tap((searchResult) => {
            // remove all invalid results (unsupported)
            // so we don't automatically jump to the system
            // these systems WILL, HOWEVER be renderd in results list!
            searchResult = (searchResult ?? []).filter(
              (system) => system.isSeSOSupported,
            );
            if (!searchResult || searchResult.length === 0) {
              // nothing found
              this.focusSearch();
            } else if (searchResult.length === 1) {
              this.blurSearch();
              // our result
              const singleHit = searchResult[0];

              // initial search did not know material number
              // we add materialnumber so it can be added to the request key
              const modifiedSearchTermForKeying = {
                systemSerialNumber: singleHit.systemSerialNumber,
                systemMaterialNumber: singleHit.systemMaterialNumber,
                modalityId: 0,
              };

              // found a system, transfer the result so we save a reuqest
              this.systemOverviewService.systemDetails.set(
                singleHit,
                modifiedSearchTermForKeying,
              );
              this.goToSystem(singleHit);
            } else {
              // multiple systems found
              this.focusSearch();
              this.modalitySettingsWereHiddenByUser$.next(false);
              this.selectedTab$.next(0);
            }
          }),
          take(1),
        )
        .subscribe();
    }
  }

  goToSystem(system: {
    systemSerialNumber: string;
    systemMaterialNumber: number;
  }): void {
    this.routeService.goToSystem(system);
    // end of search journey, move tabs back to recent systems
    this.selectedTab$.next(1);
  }

  openSearch(): void {
    this.keyboardInUse = false;
    this.routeService.openSearch(false);
    this.focusedElement$.next(this.searchInput!.nativeElement.id);
    this.focusSearch();
  }

  cancelSearch(): void {
    this.routeService.setSearchString('');
    this.focusSearch();
  }
  /**
   * implicitly trashes the current search result!
   */
  closeSearch(): void {
    this.systemOverviewService.systemSearch.reset();
    this.routeService.closeSearch();
    this.selectedTab$.next(1);
  }

  focusSearch(): void {
    this.searchInput?.nativeElement.focus();
    requestAnimationFrame(() => this.searchInput?.nativeElement.focus());
  }
  blurSearch(): void {
    this.searchInput?.nativeElement.blur();
    requestAnimationFrame(() => this.searchInput?.nativeElement.blur());
  }

  triggerSearch(): void {
    // ignore leading and trailing whitespaces
    this.searchTrigger$.next(undefined);
  }

  tabActive(i: number): null | true {
    return i === this.selectedTab$.value ? true : null;
  }
}
